import { yupResolver } from "@hookform/resolvers/yup";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

import { validateUser } from "./helper";
import { TableName } from "../../constants/table";
import TenantContext from "../../lib/context/TenantContext";
import { useFetchUsers } from "../../lib/query/userMapping";
import { addEntity } from "../../lib/services/api-service";
import useUserRole from "../activeUser";
import { FormInputText } from "../formComponent/FormInputText";

export interface IUser {
  Email: string;
  Role: string;
  Name: string;
  IsActive: boolean;
}

const AddUserDialog = (props: {
  showDialog: boolean;
  setShowDialog: React.Dispatch<boolean>;
}) => {
  const { tenant } = React.useContext(TenantContext);
  const { refetch } = useFetchUsers({ tenantId: tenant.id });
  const userRole = useUserRole();

  const schema = yup
    .object()
    .shape({
      Email: yup.string().required("Email is required").email("Invalid email format"),
      Role: yup.string().required("Role is required"),
      Name: yup.string().required("Name is required"),
      IsActive: yup.boolean().required("Status is required"),
    })
    .required();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IUser>({
    resolver: yupResolver(schema),
    defaultValues: {
      IsActive: true,
    },
  });

  const roles =
    userRole === "SuperAdmin"
      ? ["SuperAdmin", "Admin", "User"]
      : ["Admin", "User"];

  const onSubmit = async (data: IUser) => {
    const validationResult = await validateUser(tenant.id, data.Email);

    if (!validationResult.isValid) {
      toast.warn(validationResult.message);
      return;
    }

    await addEntity(TableName.Users, {
      partitionKey: tenant.id.toLowerCase(),
      rowKey: data.Email.toLowerCase(),
      Role: data.Role,
      Name: data.Name,
      IsActive: data.IsActive,
    })
      .then(() => {
        toast.success(`Successfully added new User with email: ${data.Email}`);
        refetch();
        props.setShowDialog(false);
        window.location.reload();
      })
      .catch((err) => {
        toast.warn(`Failed with error ${err}`);
        props.setShowDialog(false);
      });
  };

  const handleClose = () => {
    reset();
    props.setShowDialog(false);
  };

  return (
    <React.Fragment>
      <Dialog open={props.showDialog} onClose={handleClose}>
        <DialogTitle>
          <div className="font-semibold text-2xl justify-center items-center">
            Add User
          </div>
        </DialogTitle>

        <DialogContent sx={{ width: "400px" }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-4">
              <FormInputText name="Name" control={control} label="Name" />
              {errors.Name && <p className="mt-1 text-red-500">{errors.Name.message}</p>}
            </div>
            <div className="mt-4">
              <FormInputText name="Email" control={control} label="Email" />
              {errors.Email && <p className="mt-1 text-red-500">{errors.Email.message}</p>}
            </div>
            <div className="mt-4">
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="Role">Role</InputLabel>
                <Select
                  {...control.register("Role")}
                  size="small"
                  id="Role"
                  className={`mt-2 w-full rounded-md border  focus:border-blue-500 focus:outline-none ${errors.Role ? "border-red-500" : ""}`}
                >
                  <MenuItem value="">Select Role</MenuItem>
                  {roles.map((role, index) => (
                    <MenuItem key={index} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.Role && (
                <p className="mt-1 text-red-500">{errors.Role.message}</p>
              )}
            </div>
            <div className="mt-4">
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="IsActive">Status</InputLabel>
                <Select
                  {...control.register("IsActive")}
                  size="small"
                  id="IsActive"
                  defaultValue="true"
                  disabled
                  className={`mt-2 w-full rounded-md border  focus:border-blue-500 focus:outline-none ${errors.IsActive ? "border-red-500" : ""}`}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
              {errors.IsActive && (
                <p className="mt-1 text-red-500">{errors.IsActive.message}</p>
              )}
            </div>
            <div className="mt-6 flex items-end justify-end space-x-4">
              <Button onClick={handleClose} variant="contained">
                Discard
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Add
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default AddUserDialog;
